// redux/chatSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Chat, ChatAvatar, Message, Participant } from "@/models/chat";
import {
  getUserChatIds,
  getChatsByIds,
  createChat as createChatFirestore,
  sendMessageToDb,
  getAllParticipants,
  getOrCreateUserAdminChat,
  getTotalUserChatCount,
  listenToChats,
  fetchNextPageOfChats,
} from "@/helpers/server-actions/chat-collection";
import { RootState } from "../store";
import { getUserInfoById } from "@/helpers/server-actions/users-collection";
import { ImageConstants } from "@/helpers/utils/constants";
import { Timestamp } from "firebase/firestore";

export const defaultChatAvatar: ChatAvatar = {
  chatId: "",
  avatar: ImageConstants.CHAT_LOGO,
  name: "C-Tribe",
};

interface ChatState {
  totalCount: number;
  chats: Chat[];
  loading: boolean;
  error: string | null;
  chatAvatars: Record<string, ChatAvatar>;
  selectedChatId: string | null;
  hasMore: boolean;
  pageLength: number;
}

const initialState: ChatState = {
  totalCount: 0,
  chats: [],
  loading: false,
  error: null,
  chatAvatars: {},
  selectedChatId: null,
  hasMore: true,
  pageLength: 15,
};

export const getTotalChatCount = createAsyncThunk(
  "chat/getTotalUserChatCount",
  async (userId: string) => {
    return await getTotalUserChatCount(userId);
  }
);

export const startChatListener = createAsyncThunk(
  "chat/startChatListener",
  async (userId: string, { dispatch, getState }) => {
    const state = getState() as RootState;
    const unsubscribe = listenToChats(
      userId,
      async (chats) => {
        dispatch(handleNewChats(chats));

        const chatAvatars: Record<string, ChatAvatar> = {};
        
        for(const chat of chats) {
            switch (chat.type) {
                case 'direct':
                case 'group':
                case 'support':

                    const peerInfo = await getUserInfoById(chat.createdBy);

                    chatAvatars[chat.chatId] = {
                        chatId: chat.chatId,
                        avatar: peerInfo?.photoUrl ?? ImageConstants.CHAT_LOGO,
                        name: peerInfo?.firstName ?? 'User'
                    };

                    break;
            }
        }

        dispatch(setChatAvatars(chatAvatars));


      },
      state.chatReducer.pageLength
    );

    return unsubscribe;
  }
);
export const loadNextChatPage = createAsyncThunk(
  "chat/loadNextPage",
  async (payload: { userId: string }, { getState, dispatch }) => {
    const state = getState() as RootState;
    if (!state.chatReducer.hasMore || state.chatReducer.loading) {
      return;
    }

    dispatch(setLoading(true));

    const { userId } = payload;
    const lastVisible =
      state.chatReducer.chats[state.chatReducer.chats.length - 1].lastMessage
        ?.createdAt || new Date();
    const nextPage = await fetchNextPageOfChats(
      userId,
      Timestamp.fromDate(lastVisible),
      state.chatReducer.pageLength
    );
    dispatch(handleNewChats(nextPage));
    const chatAvatars: Record<string, ChatAvatar> = {};
    for(const chat of nextPage) {
        switch (chat.type) {
            case 'direct':
            case 'group':
            case 'support':

                const peerInfo = await getUserInfoById(chat.createdBy);

                chatAvatars[chat.chatId] = {
                    chatId: chat.chatId,
                    avatar: peerInfo?.photoUrl ?? ImageConstants.CHAT_LOGO,
                    name: peerInfo?.firstName ?? 'User'
                };

                break;
        }
    }
    dispatch(setChatAvatars(chatAvatars));
    dispatch(setLoading(false));

    return nextPage;
  }
);

// export const fetchUserChat = createAsyncThunk(
//     'chat/fetchUserChat',
//     async (userId: string, { getState }) => {
//         const rootState = getState() as RootState;
//         const chatIds = await getUserChatIds(userId);
//         const chats = await getChatsByIds(chatIds);
//         const chatAvatars: Record<string, ChatAvatar> = {};

//         await Promise.all(chats.map(async (chat) => {
//             const setChatAvatar = async (chat: Chat, chatAvatars: Record<string, ChatAvatar>, rootState: RootState) => {
//                 switch (chat.type) {
//                     case 'direct': {
//                         const participants = await getAllParticipants(chat.chatId);
//                         const peer = participants.find(p => p.userId !== rootState.appReducer.userData?.uid);
//                         if (peer) {
//                             const peerInfo = await getUserInfoById(peer.userId);
//                             if (peerInfo !== undefined) {
//                                 const name = `${peerInfo.firstName} ${peerInfo.lastName}` || 'User';
//                                 chatAvatars[chat.chatId] = {
//                                     chatId: chat.chatId,
//                                     avatar: peerInfo.photoUrl ?? ImageConstants.CHAT_LOGO,
//                                     name: name
//                                 };
//                             }
//                         }
//                         break;
//                     }
//                     case 'group':
//                         chatAvatars[chat.chatId] = {
//                             chatId: chat.chatId,
//                             avatar: ImageConstants.CHAT_LOGO,
//                             name: 'Group Chat'
//                         };
//                         break;

//                     case 'support':
//                         const peerInfo = await getUserInfoById(chat.createdBy);

//                         chatAvatars[chat.chatId] = {
//                             chatId: chat.chatId,
//                             avatar: peerInfo?.photoUrl ?? ImageConstants.CHAT_LOGO,
//                             name: peerInfo?.firstName ?? 'User'
//                         };

//                         break;
//                 }
//             };

//             await Promise.all(chats.map(async (chat) => {
//                 await setChatAvatar(chat, chatAvatars, rootState);
//             }));
//         }));

//         return { chats, chatAvatars };

//     }
// );

// Thunk to create a new admin chat
export const createAdminChat = createAsyncThunk(
  "chat/createAdminChat",
  async (userId: string, { dispatch }) => {
    const chat = await getOrCreateUserAdminChat(userId);
    dispatch(setSelectedChatId(chat.chatId));
    return chat;
  }
);

// Thunk to create a new chat
export const createChat = createAsyncThunk(
  "chat/createChat",
  async ({
    chat,
    participants,
  }: {
    chat: Chat;
    participants: Participant[];
  }) => {
    await createChatFirestore(chat, participants);
    return chat;
  }
);

// Thunk to send a message
export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async ({ chat, message }: { chat: Chat; message: Message }) => {
    await sendMessageToDb(chat, message);
    return { chat, message };
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setSelectedChatId(state, action: PayloadAction<string | null>) {
      state.selectedChatId = action.payload;
    },
    addChat(state, action: PayloadAction<Chat>) {
      state.chats.push(action.payload);
    },
    updateChat(state, action: PayloadAction<Chat>) {
      const index = state.chats.findIndex(
        (chat) => chat.chatId === action.payload.chatId
      );
      if (index !== -1) {
        state.chats[index] = action.payload;
      }
    },

    setChatData(state, action: PayloadAction<Chat[]>) {
      state.chats = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    handleNewChats(state, action: PayloadAction<Chat[]>) {
      const newChats = action.payload;
      state.hasMore = newChats.length === state.pageLength;
      const mergedChats = [...newChats, ...state.chats];

      const uniqueChats = Array.from(
        new Map(mergedChats.map((chat) => [chat.chatId, chat])).values()
      );

      uniqueChats.sort((a, b) =>
        a.lastMessage?.createdAt && b.lastMessage?.createdAt
          ? new Date(b.lastMessage?.createdAt)?.getTime() -
            new Date(a.lastMessage?.createdAt)?.getTime()
          : 0
      );

      state.chats = uniqueChats;
    },

    setChatAvatars(state, action: PayloadAction<Record<string, ChatAvatar>>) {
        state.chatAvatars = { ...state.chatAvatars, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTotalChatCount.fulfilled, (state, action) => {
        state.totalCount = action.payload;
      })
      .addCase(getTotalChatCount.rejected, (state, action) => {
        state.error =
          action.error.message || "Failed to fetch total chat count";
      })
      .addCase(createAdminChat.pending, (state) => {
        state.loading = true;
      })
      .addCase(createAdminChat.fulfilled, (state, action) => {
        state.loading = false;
        const chat = action.payload;
        const isChatExist =
          state.chats.findIndex((value) => value.chatId == chat.chatId) !== -1;

        if (!isChatExist) {
          state.chats.unshift(chat);
          state.chatAvatars[chat.chatId] = {
            chatId: chat.chatId,
            avatar: "images/C-Tribe_Main_Logo.svg",
            name: "Support Chat",
          };
        }
      })
      .addCase(createAdminChat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create admin chat";
      })
      .addCase(createChat.pending, (state) => {
        state.loading = true;
      })
      .addCase(createChat.fulfilled, (state, action) => {
        state.loading = false;
        state.chats.push(action.payload);
      })
      .addCase(createChat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create chat";
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { chat, message } = action.payload;
        if (chat) {
          const chatIndex = state.chats.findIndex(
            (c) => c.chatId === chat.chatId
          );
          if (chatIndex !== -1) {
            state.chats[chatIndex].lastMessage = message;
          }
        }
      })
      .addCase(loadNextChatPage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loadNextChatPage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load next page";
      });
  },
});

export const { addChat, updateChat, setSelectedChatId, handleNewChats, setLoading, setChatAvatars, setChatData } =
  chatSlice.actions;
export default chatSlice.reducer;
